/**
 * PDF preview component.
 * @module components/theme/PDFPreview
 */

import React from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';

// The viewer and pdf.js load only in the browser and only after the page has
// rendered, so title, description and download link come first, also for
// large PDFs.
const PDFPreviewViewer = loadable(() => import('./PDFPreviewViewer'), {
  fallback: <p className="pdf-preview-message">Vorschau wird geladen …</p>,
  ssr: false,
});

/**
 * Whether the content is a PDF file the preview can show.
 * @function isPDFFile
 * @param {object} content Content object.
 * @returns {boolean} True for a file of the type application/pdf.
 */
export const isPDFFile = (content) =>
  content?.['@type'] === 'File' &&
  content.file?.['content-type'] === 'application/pdf' &&
  !!content.file.download;

/**
 * Address of the PDF for the preview, the same one the PDF block builds for a
 * file of the site.
 * @function getPDFPreviewURL
 * @param {object} content Content object.
 * @returns {string} Download address relative to the site.
 */
export const getPDFPreviewURL = (content) =>
  flattenToAppURL(content.file.download);

// A viewer that fails to load or render must not take the page with it, the
// download link above the preview keeps working.
class PDFPreviewErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <p className="pdf-preview-message">
          Die Vorschau kann nicht angezeigt werden.
        </p>
      );
    }
    return this.props.children;
  }
}

/**
 * PDF preview component.
 * @function PDFPreview
 * @param {object} content Content object of a PDF file.
 * @returns {string} Markup of the component.
 */
const PDFPreview = ({ content }) => (
  <div className="pdf-preview">
    <PDFPreviewErrorBoundary>
      <PDFPreviewViewer url={getPDFPreviewURL(content)} />
    </PDFPreviewErrorBoundary>
  </div>
);

PDFPreview.propTypes = {
  content: PropTypes.shape({
    file: PropTypes.shape({
      download: PropTypes.string,
    }),
  }).isRequired,
};

export default PDFPreview;
